import { Component, OnInit } from '@angular/core';
import { VendorsService } from '../../../core/services/vendors.service';
import { ToastService } from '../../../core/services/toast.service';
import { firstValueFrom, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { map, take } from 'rxjs/operators';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { ModalService } from '../../../core/services/modal.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ApiError } from '../../../core/models/common/api-error';
import { CancelCondition } from '../../../core/models/entities/cancel-condition';
import { CancelConditionsService } from '../../../core/services/cancel-conditions.service';
import { cancelConditionsActions } from 'angular/app/core/store/cancel-conditions';
import { Vendor } from '../../../core/models/entities/vendor';
import { selectAuthUser, selectAuthVendor } from '../../../core/store/reducers';
import { authActions } from 'angular/app/core/store/auth';
import { fadeAnimation } from '../../../core/animations/fade.animation';

@Component({
  selector: 'app-cancel-conditions-page',
  animations: [fadeAnimation],
  template: `
    <app-page-header title="Conditions d'annulation"></app-page-header>
    
    <app-spacer size="6"></app-spacer>
    
    @if (loading$ | async) {
      <app-loader
        [centered]="true"
      ></app-loader>
    } @else {
      <div [@fadeAnimation]="'in'">
        <app-fieldset>
          <div title>Conditions d'annulation</div>
          <div description
            >Définissez ici les conditions de remboursement suite à une
            annulation.</div
            >
            <div>
              <div
                class="flex flex-col space-y-6"
                data-cy="cancel-conditions-list"
                >
                <app-list-item data-cy="full-refund-after-creation">
                  <div title>
                    <div class="font-medium uppercase"
                      >Période d'annulation gratuite
                    </div>
                    @if (!editingGracePeriod) {
                      <div
                        class="mt-2 md:max-w-[500px]"
                        >{{
                        (vendor$ | async)?.grace_period_hours > 0
                        ? 'Dans les ' +
                        (vendor$ | async)?.grace_period_hours +
                        ' heures après que le client a effectué la réservation'
                        : 'Aucune période d’annulation gratuite à compter du moment où le client a effectué sa réservation'
                        }}</div
                        >
                      }
                    </div>
                    @if (!editingGracePeriod) {
                      <div actions>
                        @if (!(vendor$ | async)?.grace_period_hours) {
                          <div>
                            <app-button
                              data-cy="add-full-refund-button"
                              icon="plus"
                              (click)="editingGracePeriod = true"
                              >Ajouter
                            </app-button>
                          </div>
                        }
                        @if (
                          (vendor$ | async)?.grace_period_hours; as gracePeriodHours
                          ) {
                          <div
                            class="space-x-4"
                            >
                            <app-icon-action
                              icon="pen"
                              [attr.data-cy]="'edit'"
                              (click)="editingGracePeriod = true"
                            ></app-icon-action>
                            <app-icon-action
                              icon="trash"
                              [attr.data-cy]="'delete'"
                              (click)="deleteGracePeriod()"
                            ></app-icon-action>
                          </div>
                        }
                      </div>
                    }
                    @if (editingGracePeriod) {
                      <app-grace-period-form
                        (onCancel)="editingGracePeriod = false"
                        (gracePeriodSaved)="editingGracePeriod = false"
                        edit
                      ></app-grace-period-form>
                    }
                  </app-list-item>
                  <!------------------------------>
                  <!--    Full reimbursement    -->
                  <!------------------------------>
                  <div>
                    <app-list-item data-cy="full-refund">
                      <div title>
                        <div class="font-medium uppercase"
                          >Remboursement intégral</div
                          >
                          @if (!editingFullReimbursement) {
                            <div class="mt-2">{{
                              (fullCancelConditions$ | async)?.length > 0
                              ? (fullCancelConditions$ | async)?.[0]?.description
                              : 'Aucun remboursement intégral'
                            }}</div>
                          }
                        </div>
                        @if (!editingFullReimbursement) {
                          <div actions>
                            @if ((fullCancelConditions$ | async)?.length === 0) {
                              <div>
                                <app-button
                                  data-cy="add-full-refund-button"
                                  icon="plus"
                                  (click)="editingFullReimbursement = true"
                                  >Ajouter
                                </app-button>
                              </div>
                            }
                            @if (
                              fullCancelConditions$ | async; as fullCancelConditions
                              ) {
                              <div
                                class="space-x-4"
                                >
                                @if (fullCancelConditions.length > 0) {
                                  <app-icon-action
                                    icon="pen"
                                    [attr.data-cy]="'edit'"
                                    (click)="editingFullReimbursement = true"
                                  ></app-icon-action>
                                }
                                @if (fullCancelConditions.length > 0) {
                                  <app-icon-action
                                    icon="trash"
                                    [attr.data-cy]="'delete'"
                        (click)="
                          deleteCancelCondition(fullCancelConditions[0].id)
                        "
                                  ></app-icon-action>
                                }
                              </div>
                            }
                          </div>
                        }
                        @if (editingFullReimbursement) {
                          <app-cancel-condition-form
                            [cancelCondition]="(fullCancelConditions$ | async)?.[0]"
                            (onCancel)="editingFullReimbursement = false"
                            (cancelConditionSaved)="editingFullReimbursement = false"
                            [cancelConditions]="allCancelConditions$ | async"
                            [fullReimbursement]="true"
                            edit
                          ></app-cancel-condition-form>
                        }
                      </app-list-item>
                    </div>
                    <!--------------------------------->
                    <!--    Partial reimbursement    -->
                    <!--------------------------------->
                    @if (
                      (partialCancelConditions$ | async)?.length === 0 &&
                      (fullCancelConditions$ | async)?.[0]?.days_interval != 0 &&
                      !creatingCancelCondition
                      ) {
                      <div
                        >
                        <app-list-item>
                          <div title>
                            <div class="font-medium uppercase"
                              >Remboursement partiel</div
                              >
                              <div class="mt-2">Aucun remboursement partiel</div>
                            </div>
                            <div actions>
                              <app-button
                                data-cy="add-partial-refund-button"
                                icon="plus"
                                (click)="creatingCancelCondition = true"
                                >Ajouter
                              </app-button>
                            </div>
                          </app-list-item>
                        </div>
                      }
                      @for (
                        cancelCondition of partialCancelConditions$ | async; track
                        cancelCondition; let i = $index) {
                        <div
                          >
                          <app-list-item [id]="i" [attr.data-cy]="'partial-refund-' + i">
                            <div title>
                              <div class="font-medium uppercase"
                                >Remboursement partiel</div
                                >
                                @if (editingCancelCondition?.id !== cancelCondition.id) {
                                  <div
                                    class="mt-2"
                                    >{{ cancelCondition.description }} :
                                    {{ cancelCondition.refund_percentage }}%
                                  </div>
                                }
                              </div>
                              @if (editingCancelCondition?.id !== cancelCondition.id) {
                                <div
                                  actions
                                  class="space-x-4"
                                  >
                                  <app-icon-action
                                    icon="pen"
                                    [attr.data-cy]="'edit'"
                                    (click)="editingCancelCondition = cancelCondition"
                                  ></app-icon-action>
                                  <app-icon-action
                                    icon="trash"
                                    [attr.data-cy]="'delete'"
                                    (click)="deleteCancelCondition(cancelCondition.id)"
                                  ></app-icon-action>
                                </div>
                              }
                              @if (editingCancelCondition?.id === cancelCondition.id) {
                                <app-cancel-condition-form
                                  [cancelCondition]="cancelCondition"
                                  (onCancel)="editingCancelCondition = null"
                                  (cancelConditionSaved)="editingCancelCondition = null"
                                  [cancelConditions]="allCancelConditions$ | async"
                                  edit
                                ></app-cancel-condition-form>
                              }
                            </app-list-item>
                          </div>
                        }
                        @if (creatingCancelCondition) {
                          <div>
                            <app-list-item>
                              <app-cancel-condition-form
                                edit
                                (onCancel)="creatingCancelCondition = false"
                                (cancelConditionSaved)="creatingCancelCondition = false"
                                [cancelConditions]="allCancelConditions$ | async"
                              ></app-cancel-condition-form>
                            </app-list-item>
                          </div>
                        }
                        @if (
                          !creatingCancelCondition &&
                          (partialCancelConditions$ | async)?.length > 0
                          ) {
                          <div
                            >
                            <app-button
                              data-cy="add-more-partial-refund-button"
                              icon="plus"
                              (click)="creatingCancelCondition = true"
                              >Remboursement partiel
                            </app-button>
                          </div>
                        }
                      </div>
                      <div class="bg-light mt-8 rounded-lg p-8">
                        <div class="mb-6 text-lg font-medium uppercase"
                          >Résumé des conditions d'annulation
                        </div>
                        @if ((vendor$ | async)?.grace_period_hours; as gracePeriod) {
                          <div
                            class="mb-1"
                            >
                            Après avoir effectué sa réservation, le client bénéficie de
                            {{ gracePeriod }} heures pour annuler gratuitement sa
                            réservation.
                          </div>
                        }
                        @for (
                          summaryLine of cancelConditionsSummaryLines$ | async; track
                          summaryLine; let j = $index) {
                          <div
                            class="mb-1"
                            data-cy="cancel-condition-line"
                            [attr.data-index]="j"
                            >{{ summaryLine }}</div
                            >
                          }
                        </div>
                      </div>
                    </app-fieldset>
                  </div>
                }
    
    `,
  standalone: false,
})
export class CancelConditionsPageComponent
  extends DestroyableComponent
  implements OnInit
{
  sendingData = false;
  editingGracePeriod = false;
  editingFullReimbursement = false;
  creatingCancelCondition = false;
  editingCancelCondition: CancelCondition;
  loading$: Observable<boolean> = this.store.select(
    fromStore.selectCancelConditionsLoading,
  );
  fullCancelConditions$: Observable<CancelCondition[]> = this.store
    .select(fromStore.selectAllCancelConditions)
    .pipe(
      map((cancelConditions) =>
        cancelConditions.filter((cc) => cc.refund_percentage == 100),
      ),
    );
  partialCancelConditions$: Observable<CancelCondition[]> = this.store
    .select(fromStore.selectAllCancelConditions)
    .pipe(
      map((cancelConditions) =>
        cancelConditions
          .filter((cc) => cc.refund_percentage != 100)
          .sort((a, b) => b.days_interval - a.days_interval),
      ),
    );
  allCancelConditions$: Observable<CancelCondition[]> = this.store
    .select(fromStore.selectAllCancelConditions)
    .pipe(
      map((cancelConditions) =>
        cancelConditions.sort((a, b) => b.days_interval - a.days_interval),
      ),
    );
  cancelConditionsSummaryLines$: Observable<string[]> = this.store.select(
    fromStore.selectCancelConditionsSummaryLines,
  );
  vendor$: Observable<Vendor> = this.store.select(selectAuthVendor);

  constructor(
    private vendorsService: VendorsService,
    private toastService: ToastService,
    private store: Store,
    private modalService: ModalService,
    private cancelConditionsService: CancelConditionsService,
  ) {
    super();
  }

  async ngOnInit() {
    this.store.dispatch(cancelConditionsActions.findAll());
  }

  deleteGracePeriod() {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Supprimer conditions d’annulation',
      message:
        'Êtes-vous sûr·e de vouloir supprimer les conditions d’annulation ?',
      okAction: {
        title: 'Supprimer',
        variant: 'danger',
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        const user = await firstValueFrom(
          this.store.select(selectAuthUser).pipe(take(1)),
        );
        const vendor = await this.vendorsService.updateGracePeriod({
          grace_period_hours: null,
        });
        this.store.dispatch(
          authActions.findCurrentUserSuccess({ user: { ...user, vendor } }),
        );
        this.toastService.success('Modifications enregistrées.');
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      }
      instance.saving = false;
      this.modalService.close();
    });
  }

  deleteCancelCondition(id: number) {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Supprimer conditions d’annulation',
      message:
        'Êtes-vous sûr·e de vouloir supprimer les conditions d’annulation ?',
      okAction: {
        title: 'Supprimer',
        variant: 'danger',
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        await this.cancelConditionsService.delete(id);
        this.store.dispatch(cancelConditionsActions.removeOne({ id }));
        this.toastService.success('Modifications enregistrées.');
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      }
      instance.saving = false;
      this.modalService.close();
    });
  }
}
